"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPoll } from "@/generated/models/ApiDropPoll";
import { preserveAuthenticatedPollVote } from "@/helpers/waves/poll-vote-reconciliation";
import { voteDropPollV2 } from "@/services/api/wave-drops-v2-api";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { PollOptionVoters } from "./PollOptionVoters";
import { PollResultOption } from "./PollResultOption";
import { PollVoteOption } from "./PollVoteOption";
import { getOptimisticPoll, getPollClosingLabel, getPollSubmitLabel } from "./WaveDropPoll.helpers";
type PollView = "vote" | "results";
interface WaveDropPollProps {
  readonly drop: ApiDrop;
}
interface LocalPollOverride {
  readonly sourcePoll: ApiDropPoll | undefined;
  readonly poll: ApiDropPoll | null;
}
interface PollInteractionState {
  readonly pollKey: string;
  readonly view: PollView;
  readonly selectedOptionNos: readonly number[];
  readonly expandedOptionNo: number | null;
}
const getDefaultPollView = (poll: ApiDropPoll): PollView => poll.is_open && poll.voted.length === 0 ? "vote" : "results";
const getVotedKey = (poll: ApiDropPoll): string => [...poll.voted].sort((a, b) => a - b).join(",");
const getPollInteractionKey = (poll: ApiDropPoll): string => `${poll.id}:${poll.is_open ? "open" : "closed"}:${getVotedKey(poll)}`;
const getDefaultPollInteractionState = (poll: ApiDropPoll): PollInteractionState => ({
  pollKey: getPollInteractionKey(poll),
  view: getDefaultPollView(poll),
  selectedOptionNos: [...poll.voted],
  expandedOptionNo: null
});
const getScopedPollInteractionState = (current: PollInteractionState | null, poll: ApiDropPoll): PollInteractionState => {
  const pollKey = getPollInteractionKey(poll);
  return current?.pollKey === pollKey ? current : getDefaultPollInteractionState(poll);
};
const getEffectivePollView = ({
  canShowResults,
  isOpen,
  scopedView
}: {
  readonly canShowResults: boolean;
  readonly isOpen: boolean;
  readonly scopedView: PollView;
}): PollView => {
  if (canShowResults && (!isOpen || scopedView === "results")) {
    return "results";
  }
  return "vote";
};
const stopPollEventPropagation = (event: SyntheticEvent) => {
  event.stopPropagation();
};
const getLocalPoll = (localPollOverride: LocalPollOverride | null, sourcePoll: ApiDropPoll | undefined): ApiDropPoll | null => {
  if (!localPollOverride) {
    return null;
  }
  if (localPollOverride.sourcePoll === sourcePoll) {
    return localPollOverride.poll;
  }
  return preserveAuthenticatedPollVote(sourcePoll, localPollOverride.poll, {
    preferExistingVote: true
  }) ?? null;
};
const UPDATED_VOTE_STATUS_DURATION_MS = 2500;
const useUpdatedVoteStatus = () => {
  const $ = _c(6);
  const showUpdatedTimeoutRef = useRef(null);
  const [showUpdated, setShowUpdated] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setShowUpdated(true);
      if (showUpdatedTimeoutRef.current) {
        globalThis.clearTimeout(showUpdatedTimeoutRef.current);
      }
      showUpdatedTimeoutRef.current = globalThis.setTimeout(() => {
        setShowUpdated(false);
        showUpdatedTimeoutRef.current = null;
      }, UPDATED_VOTE_STATUS_DURATION_MS);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const flashUpdatedVoteStatus = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (showUpdatedTimeoutRef.current) {
        globalThis.clearTimeout(showUpdatedTimeoutRef.current);
        showUpdatedTimeoutRef.current = null;
      }
      setShowUpdated(false);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const clearUpdatedVoteStatus = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (showUpdatedTimeoutRef.current) {
        globalThis.clearTimeout(showUpdatedTimeoutRef.current);
      }
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== showUpdated) {
    t4 = {
      clearUpdatedVoteStatus,
      flashUpdatedVoteStatus,
      showUpdated
    };
    $[4] = showUpdated;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export default function WaveDropPoll(t0) {
  const $ = _c(97);
  const {
    drop
  } = t0;
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const myStream = useMyStreamOptional();
  const {
    clearUpdatedVoteStatus,
    flashUpdatedVoteStatus,
    showUpdated
  } = useUpdatedVoteStatus();
  const [localPollOverride, setLocalPollOverride] = useState(null);
  let t1;
  if ($[0] !== drop.poll || $[1] !== localPollOverride) {
    t1 = getLocalPoll(localPollOverride, drop.poll);
    $[0] = drop.poll;
    $[1] = localPollOverride;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const localPoll = t1;
  const poll = localPoll ?? drop.poll;
  const [interactionState, setInteractionState] = useState(null);
  let t2;
  if ($[3] !== drop) {
    t2 = async options => await voteDropPollV2({
      drop,
      options
    });
    $[3] = drop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== drop.poll || $[6] !== myStream || $[7] !== queryClient) {
    t3 = updatedDrop => {
      if (updatedDrop.poll) {
        setLocalPollOverride({
          sourcePoll: drop.poll,
          poll: updatedDrop.poll
        });
        setInteractionState({
          ...getDefaultPollInteractionState(updatedDrop.poll),
          view: "results"
        });
      }
      updateDropInCachedDrops(queryClient, updatedDrop);
      myStream?.processIncomingDrop(updatedDrop, ProcessIncomingDropType.DROP_INSERT);
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROP_POLL_VOTERS]
      }).catch(_temp);
    };
    $[5] = drop.poll;
    $[6] = myStream;
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== clearUpdatedVoteStatus || $[10] !== setToast) {
    t4 = error => {
      clearUpdatedVoteStatus();
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
    };
    $[9] = clearUpdatedVoteStatus;
    $[10] = setToast;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const voteMutation = useMutation(t5);
  let t6;
  if ($[16] !== drop.poll || $[17] !== flashUpdatedVoteStatus || $[18] !== interactionState || $[19] !== localPollOverride || $[20] !== poll || $[21] !== requestAuth || $[22] !== voteMutation) {
    t6 = async (selectedOptionNos, t7) => {
      const {
        showUpdatedStatus: t8
      } = t7 === undefined ? {} : t7;
      const showUpdatedStatus = t8 === undefined ? false : t8;
      if (!poll?.is_open || selectedOptionNos.length === 0) {
        return false;
      }
      const {
        success
      } = await requestAuth();
      if (!success) {
        return false;
      }
      const previousLocalPollOverride = localPollOverride;
      const previousInteractionState = interactionState;
      const optimisticPoll = getOptimisticPoll(poll, selectedOptionNos);
      setLocalPollOverride({
        sourcePoll: drop.poll,
        poll: optimisticPoll
      });
      setInteractionState({
        ...getDefaultPollInteractionState(optimisticPoll),
        view: "results"
      });
      voteMutation.mutate(selectedOptionNos, {
        onSuccess: () => {
          if (showUpdatedStatus) {
            flashUpdatedVoteStatus();
          }
        },
        onError: () => {
          setLocalPollOverride(previousLocalPollOverride);
          setInteractionState(previousInteractionState);
        }
      });
      return true;
    };
    $[16] = drop.poll;
    $[17] = flashUpdatedVoteStatus;
    $[18] = interactionState;
    $[19] = localPollOverride;
    $[20] = poll;
    $[21] = requestAuth;
    $[22] = voteMutation;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const submitPollVote = t6;
  let t7;
  if ($[24] !== poll || $[25] !== submitPollVote) {
    t7 = optionNo => {
      if (!poll) {
        return;
      }
      if (!poll.multichoice) {
        const showUpdatedStatus_0 = poll.voted.length > 0;
        setInteractionState(current => {
          const scopedState = getScopedPollInteractionState(current, poll);
          return {
            ...scopedState,
            selectedOptionNos: [optionNo]
          };
        });
        submitPollVote([optionNo], {
          showUpdatedStatus: showUpdatedStatus_0
        }).then(submitted => {
          if (submitted) {
            return;
          }
          setInteractionState(current_0 => {
            const scopedState_0 = getScopedPollInteractionState(current_0, poll);
            if (scopedState_0.selectedOptionNos.length !== 1 || scopedState_0.selectedOptionNos[0] !== optionNo) {
              return scopedState_0;
            }
            return {
              ...scopedState_0,
              selectedOptionNos: [...poll.voted]
            };
          });
        }).catch(_temp2);
        return;
      }
      setInteractionState(current_1 => {
        const scopedState_1 = getScopedPollInteractionState(current_1, poll);
        const next = new Set(scopedState_1.selectedOptionNos);
        if (next.has(optionNo)) {
          next.delete(optionNo);
        } else {
          next.add(optionNo);
        }
        return {
          ...scopedState_1,
          selectedOptionNos: [...next].sort(_temp3)
        };
      });
    };
    $[24] = poll;
    $[25] = submitPollVote;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleVoteOptionChange = t7;
  let t8;
  if ($[27] !== interactionState || $[28] !== poll || $[29] !== submitPollVote) {
    t8 = async () => {
      if (!poll?.is_open) {
        return;
      }
      const scopedState_2 = getScopedPollInteractionState(interactionState, poll);
      if (scopedState_2.selectedOptionNos.length === 0) {
        return;
      }
      await submitPollVote(scopedState_2.selectedOptionNos, {
        showUpdatedStatus: poll.voted.length > 0
      });
    };
    $[27] = interactionState;
    $[28] = poll;
    $[29] = submitPollVote;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const handleSubmitVote = t8;
  let t9;
  if ($[31] !== poll) {
    t9 = () => {
      if (!poll?.is_open) {
        return;
      }
      setInteractionState(current_2 => {
        const scopedState_3 = getScopedPollInteractionState(current_2, poll);
        return {
          ...scopedState_3,
          view: "vote",
          selectedOptionNos: [...poll.voted],
          expandedOptionNo: null
        };
      });
    };
    $[31] = poll;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const showVoteView = t9;
  let t10;
  if ($[33] !== poll) {
    t10 = () => {
      if (!poll) {
        return;
      }
      setInteractionState(current_3 => {
        const scopedState_4 = getScopedPollInteractionState(current_3, poll);
        return {
          ...scopedState_4,
          view: "results",
          selectedOptionNos: [...poll.voted],
          expandedOptionNo: null
        };
      });
    };
    $[33] = poll;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const cancelVoteChange = t10;
  if (!poll) {
    return null;
  }
  const hasVoted = poll.voted.length > 0;
  let scopedState_5;
  let selectedOptions;
  let showSelectionIndicator;
  let t11;
  let totalVotes;
  let votedOptionNos;
  if ($[35] !== hasVoted || $[36] !== interactionState || $[37] !== poll) {
    scopedState_5 = getScopedPollInteractionState(interactionState, poll);
    selectedOptions = new Set(scopedState_5.selectedOptionNos);
    let t12;
    if ($[44] !== poll.options) {
      t12 = poll.options.reduce(_temp4, 0);
      $[44] = poll.options;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    totalVotes = t12;
    let t13;
    if ($[46] !== poll.voted) {
      t13 = new Set(poll.voted);
      $[46] = poll.voted;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    votedOptionNos = t13;
    showSelectionIndicator = votedOptionNos.size > 0;
    const canShowResults = !poll.is_open || hasVoted;
    t11 = getEffectivePollView({
      canShowResults,
      isOpen: poll.is_open,
      scopedView: scopedState_5.view
    });
    $[35] = hasVoted;
    $[36] = interactionState;
    $[37] = poll;
    $[38] = scopedState_5;
    $[39] = selectedOptions;
    $[40] = showSelectionIndicator;
    $[41] = t11;
    $[42] = totalVotes;
    $[43] = votedOptionNos;
  } else {
    scopedState_5 = $[38];
    selectedOptions = $[39];
    showSelectionIndicator = $[40];
    t11 = $[41];
    totalVotes = $[42];
    votedOptionNos = $[43];
  }
  const effectiveView = t11;
  const expandedOptionNo = scopedState_5.expandedOptionNo;
  const voteOptionGroupName = `drop-poll-${poll.id}`;
  let t12;
  if ($[48] !== hasVoted || $[49] !== voteMutation.isPending) {
    t12 = getPollSubmitLabel({
      isPending: voteMutation.isPending,
      hasVoted
    });
    $[48] = hasVoted;
    $[49] = voteMutation.isPending;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  const submitButtonLabel = t12;
  const showResultsFooterActions = effectiveView === "results" && hasVoted;
  const showFooterAction = poll.is_open && showResultsFooterActions;
  const isChangingVote = effectiveView === "vote" && hasVoted;
  const showVoteEditFooterAction = effectiveView === "vote" && poll.is_open && isChangingVote;
  const showMultichoiceActions = poll.multichoice && (selectedOptions.size > 0 || isChangingVote);
  const multichoiceSubmitDisabled = selectedOptions.size === 0 || voteMutation.isPending;
  let t13;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-text-[15px] tw-font-bold tw-text-white">Poll</div>;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  const t14 = `tw-size-1.5 tw-flex-shrink-0 tw-rounded-full ${poll.is_open ? "tw-bg-emerald-500" : "tw-bg-iron-600"}`;
  let t15;
  if ($[52] !== t14) {
    t15 = <span className={t14} aria-hidden="true" />;
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== poll) {
    t16 = getPollClosingLabel(poll);
    $[54] = poll;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== t16) {
    t17 = <span className="tw-truncate">{t16}</span>;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t15 || $[59] !== t17) {
    t18 = <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">{t13}<div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-iron-400">{t15}{t17}</div></div>;
    $[58] = t15;
    $[59] = t17;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== drop.id || $[62] !== effectiveView || $[63] !== expandedOptionNo || $[64] !== handleSubmitVote || $[65] !== handleVoteOptionChange || $[66] !== hasVoted || $[67] !== isChangingVote || $[68] !== multichoiceSubmitDisabled || $[69] !== poll || $[70] !== selectedOptions || $[71] !== showMultichoiceActions || $[72] !== showSelectionIndicator || $[73] !== submitButtonLabel || $[74] !== totalVotes || $[75] !== voteMutation.isPending || $[76] !== voteOptionGroupName || $[77] !== votedOptionNos) {
    t19 = effectiveView === "vote" && poll.is_open ? <div className="tw-flex tw-flex-col tw-gap-2">{poll.options.map(option_0 => <PollVoteOption key={option_0.option_no} option={option_0} checked={selectedOptions.has(option_0.option_no)} multichoice={poll.multichoice} groupName={voteOptionGroupName} disabled={voteMutation.isPending} onChange={handleVoteOptionChange} />)}{poll.multichoice && !isChangingVote && <div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-out ${showMultichoiceActions ? "tw-mt-3.5 tw-grid-rows-[1fr] tw-opacity-100" : "tw-mt-0 tw-grid-rows-[0fr] tw-opacity-0"}`}><div aria-hidden={!showMultichoiceActions} inert={!showMultichoiceActions} className="tw-overflow-hidden"><div className="tw-flex tw-gap-2"><button type="button" disabled={multichoiceSubmitDisabled} onClick={event => {
              event.stopPropagation();
              handleSubmitVote().catch(_temp5);
            }} className="tw-flex tw-flex-1 tw-transform-gpu tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white tw-bg-white tw-px-4 tw-py-2 tw-text-[13.5px] tw-font-bold tw-text-black tw-transition-all tw-duration-300 disabled:tw-cursor-not-allowed disabled:tw-border-white/[0.06] disabled:tw-bg-white/[0.025] disabled:tw-text-iron-500 desktop-hover:hover:tw-bg-iron-100">{submitButtonLabel}</button></div></div></div>}</div> : <div className="tw-flex tw-flex-col tw-gap-2">{poll.options.map(option_1 => {
        const isExpanded = option_1.votes > 0 && expandedOptionNo === option_1.option_no;
        return <div key={option_1.option_no}><PollResultOption dropId={drop.id} option={option_1} totalVotes={totalVotes} isSelected={votedOptionNos.has(option_1.option_no)} isDimmed={hasVoted && !votedOptionNos.has(option_1.option_no)} isExpanded={isExpanded} showSelectionIndicator={showSelectionIndicator} onToggle={optionNo_0 => setInteractionState(current_4 => {
            const currentState = getScopedPollInteractionState(current_4, poll);
            return {
              ...currentState,
              expandedOptionNo: currentState.expandedOptionNo === optionNo_0 ? null : optionNo_0
            };
          })} />{option_1.votes > 0 && <div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-out ${isExpanded ? "tw-mb-1 tw-mt-1 tw-grid-rows-[1fr] tw-opacity-100" : "tw-mb-0 tw-mt-0 tw-grid-rows-[0fr] tw-opacity-0"}`}><div aria-hidden={!isExpanded} inert={!isExpanded} className="tw-overflow-hidden"><div className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-3 tw-py-2.5"><PollOptionVoters dropId={drop.id} option={option_1} enabled={isExpanded} /></div></div></div>}</div>;
      })}</div>;
    $[61] = drop.id;
    $[62] = effectiveView;
    $[63] = expandedOptionNo;
    $[64] = handleSubmitVote;
    $[65] = handleVoteOptionChange;
    $[66] = hasVoted;
    $[67] = isChangingVote;
    $[68] = multichoiceSubmitDisabled;
    $[69] = poll;
    $[70] = selectedOptions;
    $[71] = showMultichoiceActions;
    $[72] = showSelectionIndicator;
    $[73] = submitButtonLabel;
    $[74] = totalVotes;
    $[75] = voteMutation.isPending;
    $[76] = voteOptionGroupName;
    $[77] = votedOptionNos;
    $[78] = t19;
  } else {
    t19 = $[78];
  }
  let t20;
  if ($[79] !== cancelVoteChange || $[80] !== handleSubmitVote || $[81] !== multichoiceSubmitDisabled || $[82] !== poll.multichoice || $[83] !== showVoteEditFooterAction || $[84] !== submitButtonLabel || $[85] !== voteMutation.isPending) {
    t20 = showVoteEditFooterAction && <div className="tw-mt-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.06] tw-pt-3.5">{poll.multichoice ? <div className="tw-flex tw-gap-2"><button type="button" disabled={voteMutation.isPending} onClick={event_0 => {
          event_0.stopPropagation();
          cancelVoteChange();
        }} className="tw-flex tw-flex-1 tw-transform-gpu tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-transparent tw-px-4 tw-py-2 tw-text-[13.5px] tw-font-medium tw-text-iron-300 tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-text-white">Cancel</button><button type="button" disabled={multichoiceSubmitDisabled} onClick={event_1 => {
          event_1.stopPropagation();
          handleSubmitVote().catch(_temp6);
        }} className="tw-flex tw-flex-1 tw-transform-gpu tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white tw-bg-white tw-px-4 tw-py-2 tw-text-[13.5px] tw-font-bold tw-text-black tw-transition-all tw-duration-300 disabled:tw-cursor-not-allowed disabled:tw-border-white/[0.06] disabled:tw-bg-white/[0.025] disabled:tw-text-iron-500 desktop-hover:hover:tw-bg-iron-100">{submitButtonLabel}</button></div> : <div className="tw-flex tw-justify-start"><button type="button" disabled={voteMutation.isPending} onClick={event_2 => {
          event_2.stopPropagation();
          cancelVoteChange();
        }} className="tw-flex tw-flex-shrink-0 tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-medium tw-text-iron-300 tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-text-white">Cancel</button></div>}</div>;
    $[79] = cancelVoteChange;
    $[80] = handleSubmitVote;
    $[81] = multichoiceSubmitDisabled;
    $[82] = poll.multichoice;
    $[83] = showVoteEditFooterAction;
    $[84] = submitButtonLabel;
    $[85] = voteMutation.isPending;
    $[86] = t20;
  } else {
    t20 = $[86];
  }
  let t21;
  if ($[87] !== showFooterAction || $[88] !== showUpdated || $[89] !== showVoteView || $[90] !== voteMutation.isPending) {
    t21 = showFooterAction && <div className="tw-mt-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.06] tw-pt-3.5"><div className="tw-flex tw-items-center tw-justify-between tw-gap-2"><button type="button" disabled={voteMutation.isPending} onClick={event_3 => {
          event_3.stopPropagation();
          showVoteView();
        }} className="tw-flex tw-flex-shrink-0 tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-medium tw-text-iron-300 tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-text-white">Change vote</button><span className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-transition-all tw-duration-300"><span className="tw-flex tw-size-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-emerald-500/30 tw-bg-emerald-500/15" aria-hidden="true"><CheckIcon className="tw-size-2.5 tw-text-emerald-400" strokeWidth={3} /></span><span className={`tw-text-[12px] tw-font-medium tw-transition-colors tw-duration-300 ${showUpdated ? "tw-text-emerald-400" : "tw-text-iron-300"}`}>{showUpdated ? "Updated" : "Voted"}</span></span></div></div>;
    $[87] = showFooterAction;
    $[88] = showUpdated;
    $[89] = showVoteView;
    $[90] = voteMutation.isPending;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] !== t18 || $[93] !== t19 || $[94] !== t20 || $[95] !== t21) {
    t22 = <div role="presentation" className="tw-mb-2 tw-mt-3 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900/80 tw-p-4 tw-shadow-lg tw-backdrop-blur md:tw-max-w-2xl" onClick={stopPollEventPropagation} onKeyDown={stopPollEventPropagation}>{t18}{t19}{t20}{t21}</div>;
    $[92] = t18;
    $[93] = t19;
    $[94] = t20;
    $[95] = t21;
    $[96] = t22;
  } else {
    t22 = $[96];
  }
  return t22;
}
function _temp6() {}
function _temp5() {}
function _temp4(total, option) {
  return total + option.votes;
}
function _temp3(a, b) {
  return a - b;
}
function _temp2() {}
function _temp() {}